import {useMix} from '@hconnect/tuc-processes/fields/mix/useMix'
import {SelectChangeEvent} from '@mui/material'
import {useState, useMemo, useEffect, useRef, useCallback} from 'react'

interface SlumpOption {
  value: string
  label: string
}

interface SetControlOption {
  value: string
  label: string
}

interface UseMaterialVariationsFormReturn {
  // Current values
  slump: string
  setControl: string
  // Change handlers
  handleSlumpChange: (event: SelectChangeEvent<string>) => void
  handleSetControlChange: (event: SelectChangeEvent<string>) => void
  // Options for dropdowns
  slumpOptions: SlumpOption[]
  setControlOptions: SetControlOption[]
  // Availability flags
  isSlumpAvailable: boolean
  isSetControlAvailable: boolean
  isEnabled: boolean
  // Selected material info
  selectedMaterialDescription: string
}

/**
 * Custom hook for managing material variations form state.
 *
 * Handles:
 * - Syncing local UI state with form context
 * - Proper reset behavior (clears to empty, not defaults)
 * - Initial population from order defaults on first mount
 * - Dropdown options with sorting
 */
export const useMaterialVariationsForm = (): UseMaterialVariationsFormReturn => {
  const {
    availableMaterialVariations,
    selectedMaterialVariations,
    defaultMaterial,
    defaultMaterialVariations,
    setMaterialVariation,
    selectedMaterial
  } = useMix()

  // Check if we're still on the original material from the order
  const isOnDefaultMaterial = selectedMaterial?.materialId === defaultMaterial?.materialId

  // Extract primitive values for dependency tracking
  const selectedSlumpValue = selectedMaterialVariations?.slump?.value
  const selectedSetControlValue = selectedMaterialVariations?.setControl?.value
  // Only use default values if we're still on the default material
  const defaultSlumpValue = isOnDefaultMaterial
    ? defaultMaterialVariations?.slump?.value
    : undefined
  const defaultSetControlValue = isOnDefaultMaterial
    ? defaultMaterialVariations?.setControl?.value
    : undefined
  const selectedMaterialId = selectedMaterial?.materialId

  // Local state for UI selections
  const [slump, setSlump] = useState<string>('')
  const [setControl, setSetControl] = useState<string>('')

  // Track if local state has been initialized with defaults
  const hasLocalStateInitialized = useRef(false)
  // Track the material ID to detect material changes
  const previousMaterialId = useRef<string | undefined | null>(undefined)

  // Reset initialization when material changes
  useEffect(() => {
    if (
      previousMaterialId.current !== undefined &&
      previousMaterialId.current !== selectedMaterialId
    ) {
      // Material changed - reset initialization flag and clear local state
      hasLocalStateInitialized.current = false
      setSlump('')
      setSetControl('')
    }
    previousMaterialId.current = selectedMaterialId
  }, [selectedMaterialId])

  // Sync local state with form context
  // On first mount: use defaults as fallback for pre-existing order values (only if on default material)
  // On subsequent updates (including reset): only use selected values
  useEffect(() => {
    if (!hasLocalStateInitialized.current) {
      hasLocalStateInitialized.current = true
      setSlump(selectedSlumpValue || defaultSlumpValue || '')
      setSetControl(selectedSetControlValue || defaultSetControlValue || '')
    } else {
      setSlump(selectedSlumpValue || '')
      setSetControl(selectedSetControlValue || '')
    }
  }, [selectedSlumpValue, selectedSetControlValue, defaultSlumpValue, defaultSetControlValue])

  // Change handlers
  const handleSlumpChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newValue = event.target.value
      setSlump(newValue)
      void setMaterialVariation(newValue, 'slump')
    },
    [setMaterialVariation]
  )

  const handleSetControlChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newValue = event.target.value
      setSetControl(newValue)
      void setMaterialVariation(newValue, 'setControl')
    },
    [setMaterialVariation]
  )

  // Get slump options from material variations (sorted numerically)
  const slumpOptions = useMemo<SlumpOption[]>(() => {
    if (!availableMaterialVariations.isEnabled || !availableMaterialVariations.isSlumpAvailable) {
      return []
    }
    return availableMaterialVariations.slumps
      .map((s) => ({
        value: s.value,
        label: s.description || s.value
      }))
      .sort((a, b) => {
        const numA = parseFloat(a.value)
        const numB = parseFloat(b.value)
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB
        }
        return a.value.localeCompare(b.value)
      })
  }, [availableMaterialVariations])

  // Get set control options from material variations
  const setControlOptions = useMemo<SetControlOption[]>(() => {
    if (
      !availableMaterialVariations.isEnabled ||
      !availableMaterialVariations.isSetControlAvailable
    ) {
      return []
    }
    return availableMaterialVariations.setControls.map((s) => ({
      value: s.value,
      label: s.description || s.value
    }))
  }, [availableMaterialVariations])

  return {
    slump,
    setControl,
    handleSlumpChange,
    handleSetControlChange,
    slumpOptions,
    setControlOptions,
    isSlumpAvailable:
      availableMaterialVariations.isEnabled && availableMaterialVariations.isSlumpAvailable,
    isSetControlAvailable:
      availableMaterialVariations.isEnabled && availableMaterialVariations.isSetControlAvailable,
    isEnabled: availableMaterialVariations.isEnabled,
    selectedMaterialDescription: selectedMaterial?.materialDescription || ''
  }
}
